// B10 S17 · the Action Center, full (CR-092 "Prioritized actions across reqs, candidates, scheduling, packets,
// scorecards, panels, approvals, offers and closeout. Deep link to exact screen; close automatically when resolved;
// retain history."; plan D11; DC-44…DC-47, DC-214). `#action-center` is a route B1's shell will own: this is its B10
// fallback (C-B10-10; B1's views/ActionCenter.tsx and views/actions/ActionRow.tsx are not on the lane and are not
// touched). It reads B1's `action_center.list` and composes D0 only: PageHeader, route Tabs *Prioritized actions* ·
// *History* with their counts (DC-46: no dismiss anywhere), the kind chips, the FilterBar, S19's *Saved views* on surface
// `action_center`, then the open rows under the tier headings *Breach — dashboard risk · Near-breach · Reminder · Due*
// (DC-45, tier first [default home Q4]) or the History rows (*Completed*, by whom, when, linked to the timeline event,
// DC-47). Each open row is one deep link to the exact screen (DC-44); a row the reader resolved elsewhere leaves the
// list in D0's 200 ms collapse when the page reads again (DC-47). `#action-center?kind=approvals` is *Approvals*
// (DC-214): the same rows, once.
//
// The page takes its query and its navigation from register.ts, and S19's select as a component, so this module
// loads without the router or S19's file (the slice's SSR test renders it). Hotspot keys: `ac.*` (B1 addendum grammar).

import { McPage } from "@/shell/page";
import { useCallback, useEffect, useId, useRef, useState, type ComponentType } from "react";
import {
  ActionRow, ActionRows, ErrorStrip, PageHeader, SectionTitle, Stack, Tabs, useCaptureSettle, type TabsItem,
} from "@/mc";
import { errorText } from "@/api/useTool";
import {
  OPEN_PAGES, countsOf, groupByTier, historyRowProps, openCountOf, openRowProps, rowsOf, useActionPages,
  type ActionCenterRow, type ActionPagesInput,
} from "./api";
import {
  ActionFilterBar, KindChips, actionHref, fromSpec, isApprovals, pageInput, toSpec, type ActionQuery, type SeenRequisition,
} from "./Filters";
import { PAGE_WORDS, TIER_HEADING } from "./labels";

/** S19's `<SavedViewsSelect>` (views/b10/lib/savedViews.tsx, PARALLEL-MAP §2.1), handed in by register.ts. */
export type SavedViewsSelectView = ComponentType<{
  surface: string;
  current: Record<string, unknown>;
  onApply: (spec: Record<string, unknown>, dropped: string[]) => void;
  hs?: string;
}>;

export interface ActionCenterPageProps {
  /** The route's query, parsed (Filters.parseActionQuery). */
  query: ActionQuery;
  /** Goes to another hash (the router's `go`). */
  navigate: (to: string) => void;
  /** Absent: no *Saved views* control. */
  SavedViews?: SavedViewsSelectView | undefined;
}

export function ActionCenterPage(props: ActionCenterPageProps) {
  return (
    <McPage>
      <ActionCenter {...props} />
    </McPage>
  );
}

function ActionCenter({ query, navigate, SavedViews }: ActionCenterPageProps) {
  const settle = useCaptureSettle();
  const open = query.tab === "open";
  const input = pageInput(query);
  const pages = useActionPages(input, settle);
  const rows = rowsOf(pages.data);
  const counts = countsOf(pages.data);
  const requisitions = useSeenRequisitions(rows, query.requisition);
  const change = useCallback((next: ActionQuery) => navigate(actionHref(next)), [navigate]);

  // Prioritized actions: read every page B1 resolves, so each tier group is whole.
  const read = pages.data?.pages.length ?? 0;
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = pages;
  useEffect(() => {
    if (open && hasNextPage && !isFetchingNextPage && read < OPEN_PAGES) void fetchNextPage();
  }, [open, hasNextPage, isFetchingNextPage, read, fetchNextPage]);

  const title = isApprovals(query) ? PAGE_WORDS.approvals : PAGE_WORDS.actionCenter;
  // Each tab counts the rows its list holds under the same filters (B1's `open` is before the kind and tier filters).
  const listed = open && pages.data && !hasNextPage ? rows.length : undefined;
  const tabs: TabsItem[] = [
    { key: "open", label: PAGE_WORDS.prioritizedActions, count: openCountOf(counts, query, listed), href: actionHref({ ...query, tab: "open" }), hs: "ac.tab.open" },
    { key: "history", label: PAGE_WORDS.history, count: counts?.["history"], href: actionHref({ ...query, tab: "history" }), hs: "ac.tab.history" },
  ];
  const savedViews = SavedViews ? (
    <SavedViews
      surface="action_center"
      current={toSpec(query)}
      onApply={(spec) => change(fromSpec(spec, query.tab))}
      
    />
  ) : null;
  const failure = pages.error;

  return (
    <Stack gap={16}>
      <PageHeader title={title} />
      <Tabs route label={title} items={tabs} active={query.tab} />
      <KindChips query={query} counts={open ? counts : undefined} onChange={change} />
      <ActionFilterBar query={query} requisitions={requisitions} onChange={change} savedViews={savedViews} />
      {failure ? (
        <ErrorStrip
          message={errorText(failure)}
          problem={{ status: failure.status, refusal_code: failure.code, detail: failure.message, request_id: failure.requestId }}
          onRetry={() => void pages.refetch()}
        />
      ) : null}
      {failure && !pages.data ? null : open ? (
        <TierGroups rows={rows} input={input} loading={pages.isPending} />
      ) : (
        <HistoryRows
          rows={rows}
          loading={pages.isPending}
          more={Boolean(pages.hasNextPage)}
          fetching={pages.isFetchingNextPage}
          onMore={fetchNextPage}
        />
      )}
    </Stack>
  );
}

/** The requisitions of the rows read so far (and the one filtered on), for the requisition filter, by id. */
function useSeenRequisitions(rows: readonly ActionCenterRow[], selected: string | null): SeenRequisition[] {
  const seen = useRef(new Map<string, string | null>());
  for (const row of rows) {
    if (!row.requisition_id) continue;
    if (!seen.current.has(row.requisition_id) || (seen.current.get(row.requisition_id) === null && row.requisition_title)) {
      seen.current.set(row.requisition_id, row.requisition_title ?? null);
    }
  }
  if (selected && !seen.current.has(selected)) seen.current.set(selected, null);
  return [...seen.current.entries()]
    .map(([id, title]) => ({ id, title }))
    .sort((a, b) => Number(a.id.slice(2)) - Number(b.id.slice(2)));
}

/**
 * DC-47 "Auto-close is silent in the list (200 ms collapse)": a row that was open on the last read of the same query and
 * is gone from this one was resolved; it stays in its place, closing, until its collapse ends. A changed query (another
 * filter, another tab) is a new list, not a resolution.
 */
interface Leaving {
  row: ActionCenterRow;
  /** Where it stood on the read it was last seen in. */
  at: number;
}

function useLeaving(rows: readonly ActionCenterRow[], input: ActionPagesInput) {
  const key = JSON.stringify(input);
  const last = useRef<{ key: string; ids: string }>({ key, ids: "" });
  const previous = useRef<readonly ActionCenterRow[]>(rows);
  const [leaving, setLeaving] = useState<{ key: string; rows: Leaving[] }>({ key, rows: [] });
  const ids = rows.map((row) => row.id).join(" ");
  useEffect(() => {
    const before = last.current;
    const was = previous.current;
    last.current = { key, ids };
    previous.current = rows;
    if (before.key !== key) {
      setLeaving({ key, rows: [] });
      return;
    }
    if (before.ids === ids) return;
    const now = new Set(rows.map((row) => row.id));
    const gone = was.flatMap((row, at) => (now.has(row.id) ? [] : [{ row, at }]));
    if (gone.length) {
      setLeaving((state) => ({ key, rows: [...state.rows.filter((each) => !now.has(each.row.id)), ...gone] }));
    }
    // `rows` is rebuilt on every render; `ids` says when the list itself changed.
  }, [key, ids]);
  const closed = useCallback((id: string) => {
    setLeaving((state) => ({ ...state, rows: state.rows.filter((each) => each.row.id !== id) }));
  }, []);
  const current = leaving.key === key ? leaving.rows : [];
  // Each leaving row goes back where it stood; groupByTier keeps that order inside its tier group.
  const shown: ActionCenterRow[] = [...rows];
  for (const { row, at } of [...current].sort((a, b) => a.at - b.at)) {
    if (!shown.some((each) => each.id === row.id)) shown.splice(Math.min(at, shown.length), 0, row);
  }
  return { shown, closing: new Set(current.map((each) => each.row.id)), closed };
}

function TierGroups({ rows, input, loading }: { rows: readonly ActionCenterRow[]; input: ActionPagesInput; loading: boolean }) {
  const base = useId();
  const { shown, closing, closed } = useLeaving(rows, input);
  if (loading) return <ActionRows loading={3} />;
  const groups = groupByTier(shown);
  if (groups.length === 0) return <ActionRows />;
  let start = 0;
  const numbered = groups.map((group) => {
    const first = start;
    start += group.rows.length;
    return { ...group, first };
  });
  return (
    <Stack gap={20}>
      {numbered.map((group) => {
        const id = `${base}-${group.tier}`;
        return (
          <section key={group.tier} aria-labelledby={id}>
            <SectionTitle as="h2" id={id} count={group.rows.filter((row) => !closing.has(row.id)).length}>
              {TIER_HEADING[group.tier]}
            </SectionTitle>
            <ActionRows labelledBy={id}>
              {group.rows.map((row, at) => (
                <ActionRow
                  key={row.id}
                  {...openRowProps(row, group.first + at)}
                  closing={closing.has(row.id)}
                  onClosed={() => closed(row.id)}
                />
              ))}
            </ActionRows>
          </section>
        );
      })}
    </Stack>
  );
}

/** The History tab (DC-46, DC-47): closed rows, newest first, never deleted; the next page loads at the end of the list. */
function HistoryRows({ rows, loading, more, fetching, onMore }: {
  rows: readonly ActionCenterRow[];
  loading: boolean;
  more: boolean;
  fetching: boolean;
  onMore: () => unknown;
}) {
  if (loading) return <ActionRows loading={3} />;
  return (
    <Stack gap={10}>
      <ActionRows>{rows.map((row, at) => <ActionRow key={row.id} {...historyRowProps(row, at)} />)}</ActionRows>
      {fetching ? <ActionRows loading={2} /> : more ? <AtEnd onVisible={onMore} /> : null}
    </Stack>
  );
}

/** An empty marker after the last row: when it scrolls into view, the next page is read. */
function AtEnd({ onVisible }: { onVisible: () => unknown }) {
  const marker = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const element = marker.current;
    if (!element || typeof IntersectionObserver === "undefined") return;
    const watch = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void onVisible();
    });
    watch.observe(element);
    return () => watch.disconnect();
  }, [onVisible]);
  return <div ref={marker} aria-hidden="true" />;
}
